import React, { useMemo } from 'react';
import type { TradeItem } from '@/types';
import { FilterPill } from './FilterPillPanel';
import { aggregateByCurrency } from '@/utils/trades/currency-totals';
import {
  getEffectiveSettlementCurrency,
  getSettlementTotalValue,
} from '@/utils/trades/settlement-ccy';
import { getExchangeRate, getExchangeRateSource } from '@/services/currency/currency-service';
import { approxRateNote, APPROX_MATERIAL_SHARE } from '@/utils/trades/footer-totals';
import { formatCurrency } from '@/utils/trades/mtm-calculations';
import { useCurrencyDisplayStore, useFxRatesStore } from '@/stores';

export interface CurrencyFilterPillProps {
  /**
   * The currency-pickable basis: the view/subtab-scoped book with every OTHER
   * active filter applied but NOT the currency filter, so every currency present
   * stays visible (and pickable) regardless of the current selection.
   *
   * The windowed trades blotter passes `serverPerCcy` instead (whole filtered
   * book); MTM (whole-CP book in memory) passes `rows`.
   */
  rows?: TradeItem[];
  /** Server-computed per-ccy aggregate over the whole book — REPLACES the `rows`
   *  aggregation when set. `serverUnconvertibleCcys` = the ⚠ buckets. */
  serverPerCcy?: PerCcyEntry[];
  serverUnconvertibleCcys?: string[];
  /** Currently selected settlement currencies (mirrors the page's
   *  `settlementCurrencies` filter value — LR-474 D3). */
  selected: string[];
  onChange: (currencies: string[]) => void;
  /** Per-row value contribution in the row's EFFECTIVE SETTLEMENT currency
   *  (defaults to the settlement Total Value). NB the unconvertible-row ⚠
   *  detection is keyed to the DEFAULT valueFn (booked-fx total value); a
   *  custom twin-based valueFn that converts without fx_rate may see a false
   *  ⚠ on degenerate payloads — acceptable for an approximate-marker. */
  valueFn?: (t: TradeItem) => number;
  /** MTM only: unchecked rows excluded from the per-currency counts/values. */
  excludedIds?: Set<number>;
  className?: string;
}

interface PerCcyEntry {
  ccy: string;
  count: number;
  value: number;
}

/**
 * Compact currency filter living in the summary bar (LR-469 → LR-474). Dropdown
 * lists every EFFECTIVE SETTLEMENT currency in the current slice with its trade
 * count and settlement-denominated value; the selection drives the page's
 * `settlementCurrencies` filter (the sidebar's Security Currency section is a
 * separate filter on the security ccy — D3). The footer rolls the *effective*
 * slice (selected currencies, or all when none selected) up to a single EUR/USD
 * equivalent — flagged `≈` when any conversion fell back to a stale rate.
 *
 * Interaction model (LR-474 WP4 — replaces the old single-button row whose
 * hidden "click a checked row while All → isolate" behaviour confused users):
 *   - clicking a currency NAME isolates it (only that ccy); clicking the name
 *     of the already-isolated currency returns to All;
 *   - clicking the CHECKBOX toggles membership (from All, unchecking one ccy
 *     selects "all except it"); toggling the selection back to the full set —
 *     or unchecking the last remaining ccy — normalises to All (`[]`);
 *   - header `Select all` returns to All; `Clear` unchecks every box as a
 *     transient draft (the filter is untouched until the next pick), so the
 *     user can rebuild a small selection from zero.
 * Store semantics are unchanged: `selected = []` ⇔ All.
 */
export const CurrencyFilterPill: React.FC<CurrencyFilterPillProps> = ({
  rows,
  serverPerCcy,
  serverUnconvertibleCcys,
  selected,
  onChange,
  valueFn,
  excludedIds,
  className,
}) => {
  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  const toggleBaseCurrency = useCurrencyDisplayStore((s) => s.toggleBaseCurrency);
  // Reactive FX signal: recompute the Σ≈ roll-up when live rates land after a cold-load
  // race, instead of staying frozen on the fallback figure + ⚠ (LR-469 hooks-1).
  const fxVersion = useFxRatesStore((s) => s.version);

  const { perCcy, unconvertibleCcys } = useMemo(() => {
    // Server aggregate wins (whole book); `rows` is the MTM in-memory path.
    if (serverPerCcy) {
      return { perCcy: serverPerCcy, unconvertibleCcys: new Set(serverUnconvertibleCcys ?? []) };
    }
    const source = rows ?? [];
    const included =
      excludedIds && excludedIds.size > 0 ? source.filter((r) => !excludedIds.has(r.id)) : source;
    const counts: Record<string, number> = {};
    // r1 #1 residual: a cross-ccy row without a usable fx_rate contributes a
    // NATIVE figure to its settlement bucket below (the key must stay in the
    // filter's domain, so an honest re-key is not an option here) — collect
    // WHICH buckets are tainted so the Σ≈ rollup can flag ⚠ only when a
    // tainted bucket is actually part of the rolled-up selection (r2 #3-verify).
    const tainted = new Set<string>();
    for (const t of included) {
      // LR-474: rows COUNT under their EFFECTIVE settlement ccy — that is the
      // settlementCurrencies filter's domain, so the row list and the predicate
      // can never disagree about which bucket a trade belongs to.
      const ccy = getEffectiveSettlementCurrency(t) || baseCurrency.toUpperCase();
      counts[ccy] = (counts[ccy] ?? 0) + 1;
      if (!getSettlementTotalValue(t).converted) tainted.add(ccy);
    }
    const agg = aggregateByCurrency(
      included,
      valueFn ?? ((t) => getSettlementTotalValue(t).value),
      baseCurrency,
      getEffectiveSettlementCurrency,
    );
    return {
      perCcy: agg.currencies.map((ccy) => ({ ccy, count: counts[ccy] ?? 0, value: agg.byCcy[ccy] ?? 0 })),
      unconvertibleCcys: tainted,
    };
  }, [rows, serverPerCcy, serverUnconvertibleCcys, excludedIds, valueFn, baseCurrency]);

  // Roll up the EFFECTIVE slice: the selected currencies, or all present when the
  // selection is empty (= "All"). Native per-currency figures above never convert;
  // only this convenience total does.
  const rollup = useMemo(() => {
    let equivCents = 0;
    let grossCents = 0; // Σ|bucket| — materiality denominator (see footer-totals)
    let approxCents = 0; // Σ|non-live bucket| — materiality numerator
    let unconvertible = false; // a bucket carries a native figure that could not be booked-fx converted
    const approxSet = new Set<string>();
    for (const { ccy, value } of perCcy) {
      if (selected.length > 0 && !selected.includes(ccy)) continue;
      // A bucket containing an unconvertible native contribution (r1 #1
      // residual) makes the roll-up imprecise only when it is actually
      // included in the effective slice. This is a wrong-denomination issue,
      // not a stale-rate one — it always flags (not materiality-gated).
      if (unconvertibleCcys.has(ccy)) unconvertible = true;
      let bucketCents: number;
      if (ccy === baseCurrency) {
        bucketCents = Math.round(value * 100);
      } else {
        if (getExchangeRateSource(ccy, baseCurrency) !== 'live') approxSet.add(ccy);
        bucketCents = Math.round(value * getExchangeRate(ccy, baseCurrency) * 100);
      }
      equivCents += bucketCents;
      grossCents += Math.abs(bucketCents);
      if (approxSet.has(ccy)) approxCents += Math.abs(bucketCents);
    }
    // Stale-FX ⚠ only when the non-live slice is material (same rule as the
    // footer/metrics totals — a sub-threshold un-fed tail doesn't warrant it).
    const approxShare = grossCents > 0 ? approxCents / grossCents : 0;
    const fxApprox = approxSet.size > 0 && approxShare >= APPROX_MATERIAL_SHARE;
    return {
      equiv: equivCents / 100,
      usedFallback: unconvertible || fxApprox,
      approxCurrencies: fxApprox ? [...approxSet].sort() : [],
    };
    // fxVersion is an intentional extra dep (unused in the body): the roll-up reads live FX
    // through currency-service module state, so this forces a recompute when rates land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCcy, unconvertibleCcys, selected, baseCurrency, fxVersion]);

  const buttonLabel =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? selected[0]
        : `${selected[0]} +${selected.length - 1}`;

  const entries = useMemo(
    () =>
      perCcy.map(({ ccy, count, value }) => ({
        key: ccy,
        count,
        trailing: formatCurrency(value, ccy),
      })),
    [perCcy],
  );

  return (
    <FilterPill
      className={className}
      // LR-474 r1 #7: explicit — this is the SETTLEMENT-ccy filter; the sidebar's
      // 'Security Currency' section is the other one (D3).
      caption="Settlement Ccy:"
      buttonLabel={buttonLabel}
      title="Filter by settlement currency"
      entries={entries}
      selected={selected}
      onChange={onChange}
      isolateTitle={(ccy, isolated) => (isolated ? 'Show all currencies' : `Only ${ccy}`)}
      emptyMessage="No currencies in view"
      footer={({ draftCleared }) => (
        <CurrencyRollupFooter
          draftCleared={draftCleared}
          rollup={rollup}
          baseCurrency={baseCurrency}
          toggleBaseCurrency={toggleBaseCurrency}
        />
      )}
    />
  );
};

/** The Σ≈ roll-up + EUR/USD base toggle under the currency list. */
const CurrencyRollupFooter: React.FC<{
  draftCleared: boolean;
  rollup: { equiv: number; usedFallback: boolean; approxCurrencies: string[] };
  baseCurrency: string;
  toggleBaseCurrency: () => void;
}> = ({ draftCleared, rollup, baseCurrency, toggleBaseCurrency }) => (
  <>
    {/* r1 #3: while the Clear draft is active the Σ≈ of the UNCHANGED filter
        would contradict the all-unchecked boxes above — explain instead. */}
    {draftCleared ? (
      <span className="text-xs text-slate-400">
        Nothing selected — pick a currency (closing keeps the current filter)
      </span>
    ) : (
      <span className="text-xs font-medium text-slate-500">
        Σ ≈ {formatCurrency(rollup.equiv, baseCurrency)}
        {rollup.usedFallback && (() => {
          // Name the stale-FX currencies when that's the cause; fall back to the
          // booked-rate wording when only an unconvertible bucket flagged it.
          const note = rollup.approxCurrencies.length
            ? approxRateNote(rollup.approxCurrencies)
            : 'Approximate — some figures could not be converted at the booked trade rate';
          return (
            <span className="ml-1 text-amber-600" role="img" aria-label={note} title={note}>
              ⚠
            </span>
          );
        })()}
      </span>
    )}
    <button
      type="button"
      onClick={toggleBaseCurrency}
      className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
      title="Toggle roll-up base currency"
    >
      {baseCurrency} ⇄
    </button>
  </>
);
